import { readSheet } from 'read-excel-file/browser'
import { readPercentageCellOverrides } from './excelCellFormatting'

export const QUESTION_EXCEL_HEADERS = [
  'question',
  'option A',
  'option B',
  'option C',
  'option D',
  'option E',
  'option A version B',
  'option B version B',
  'option C version B',
  'option D version B',
  'option E version B',
  'correct answer',
  'correct point',
  'question_mark',
  'question_type',
  'group',
  'article'
]

const OPTIONAL_QUESTION_EXCEL_HEADERS = [
  'question_type',
  'option A version B',
  'option B version B',
  'option C version B',
  'option D version B',
  'option E version B',
  'group',
  'article'
]

const REQUIRED_QUESTION_EXCEL_HEADERS = QUESTION_EXCEL_HEADERS.filter(
  header => !OPTIONAL_QUESTION_EXCEL_HEADERS.includes(header)
)

const QUESTION_TYPES = ['select', 'input']

function getCellText (value) {
  if (value === null || value === undefined) {
    return ''
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE'
  }

  if (typeof value === 'number') {
    return numberToPlainText(value)
  }

  return String(value)
}

function numberToPlainText (value) {
  const text = String(value)

  if (!/[eE]/.test(text)) {
    return text
  }

  const [coefficient, exponentText] = text.toLowerCase().split('e')
  const exponent = Number(exponentText)
  const sign = coefficient.startsWith('-') ? '-' : ''
  const unsignedCoefficient = sign ? coefficient.slice(1) : coefficient
  const [integer, fraction = ''] = unsignedCoefficient.split('.')
  const digits = `${integer}${fraction}`
  const decimalIndex = integer.length + exponent

  if (decimalIndex <= 0) {
    return `${sign}0.${'0'.repeat(-decimalIndex)}${digits}`
  }

  if (decimalIndex >= digits.length) {
    return `${sign}${digits}${'0'.repeat(decimalIndex - digits.length)}`
  }

  return `${sign}${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`
}

function normalizeExcelNumber (value) {
  const number = Number(value)

  if (!Number.isFinite(number)) {
    return value
  }

  // Excel numbers have at most 15 significant digits. Normalizing to that
  // precision removes serialization artifacts such as 0.10000000000000001.
  return numberToPlainText(Number(number.toPrecision(15)))
}

function normalizeHeader (value) {
  return getCellText(value).replace(/^\uFEFF/, '').trim()
}

export function parseQuestionRows (sheetRows) {
  const rows = sheetRows
    .map((values, index) => ({
      excelRow: index + 1,
      values: values.map(getCellText)
    }))
    .filter(({ values }) => values.some(value => value !== ''))

  if (rows.length < 2) {
    throw new Error('Excel 中没有可导入的题目，请保留表头并至少填写一行题目')
  }

  const headers = rows[0].values.map(normalizeHeader)
  const duplicateHeaders = headers.filter(
    (header, index) => header && headers.indexOf(header) !== index
  )

  if (duplicateHeaders.length > 0) {
    throw new Error(`Excel 表头重复：${Array.from(new Set(duplicateHeaders)).join('、')}`)
  }

  const missingHeaders = REQUIRED_QUESTION_EXCEL_HEADERS.filter(
    header => !headers.includes(header)
  )

  if (missingHeaders.length > 0) {
    throw new Error(`Excel 缺少必要表头：${missingHeaders.join('、')}`)
  }

  return rows.slice(1).map(({ excelRow, values }) => {
    const question = {}

    QUESTION_EXCEL_HEADERS.forEach(header => {
      question[header] = values[headers.indexOf(header)] || ''
    })

    question.question_type = question.question_type.trim().toLowerCase() || 'select'

    if (!QUESTION_TYPES.includes(question.question_type)) {
      throw new Error(
        `Excel 第 ${excelRow} 行的 question_type 无效，只能填写 select 或 input`
      )
    }

    if (!question.question.trim()) {
      throw new Error(`Excel 第 ${excelRow} 行的 question 为空`)
    }

    return question
  })
}

export async function parseQuestionExcel (file) {
  if (!file) {
    throw new Error('请选择需要导入的 Excel 文件')
  }

  if (!/\.xlsx$/i.test(file.name)) {
    throw new Error('仅支持标准 .xlsx 格式的 Excel 文件')
  }

  let rows

  try {
    const [sheetRows, percentageOverrides] = await Promise.all([
      readSheet(file, {
        trim: false,
        parseNumber: normalizeExcelNumber
      }),
      readPercentageCellOverrides(file)
    ])

    percentageOverrides.forEach(({ rowIndex, columnIndex, text }) => {
      if (sheetRows[rowIndex] && columnIndex < sheetRows[rowIndex].length) {
        sheetRows[rowIndex][columnIndex] = text
      }
    })

    rows = sheetRows
  } catch (error) {
    throw new Error('无法解析 Excel 文件，请确认文件未损坏且格式为 .xlsx')
  }

  return parseQuestionRows(rows)
}
